import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Sprout, Languages } from "lucide-react";
import InstallPrompt from "@/components/layout/InstallPrompt";
import ContactFooter from "@/components/layout/ContactFooter";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setHeroIndex((i) => (i + 1) % 2), 7000);
    return () => clearInterval(iv);
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) { toast.success(t("welcome")); nav("/"); }
    else toast.error(r.error);
  }

  async function onRegister(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data.token) localStorage.setItem("agrinova_token", data.token);
      toast.success(t("account_created"));
      window.location.href = "/decouvrir";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  }

  async function onForgot(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      toast.success(t("reset_email_sent"));
      setMode("login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left */}
      <div className="hidden md:block relative overflow-hidden">
        {["/login-hero.jpeg", "/login-hero-2.jpeg"].map((src, i) => (
          <img
            key={src}
            alt="Exploitation agriculture & pisciculture AgriNova"
            data-testid={i === 0 ? "login-hero" : `login-hero-${i + 1}`}
            src={src}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2500ms] ease-in-out"
            style={{ opacity: heroIndex === i ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="texture-topo-overlay" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-primary-foreground">
          <div className="flex items-center gap-3">
            <img src="/logo-agrinova.png" alt="AgriNova" className="w-14 h-14 rounded-lg object-cover" data-testid="login-logo" />
            <div>
              <div className="text-xl font-bold">AgriNova</div>
              <div className="text-xs uppercase tracking-[0.25em] opacity-80">{t("slogan")}</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-3">
              {t("hero_title")}
            </h1>
            <p className="text-primary-foreground/80 text-base max-w-md">
              {t("hero_sub")}
            </p>
          </div>
        </div>
      </div>
      {/* Right */}
      <div className="flex items-center justify-center p-6 md:p-12 texture-leaves-faint">
        <div className="w-full max-w-md relative z-10">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2 md:hidden">
              <img src="/logo-agrinova.png" alt="AgriNova" className="w-9 h-9 rounded-lg object-cover bg-white" />
              <span className="font-bold">AgriNova</span>
            </div>
            <button
              data-testid="lang-toggle-login"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="ml-auto flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "fr" ? "English" : "Français"}
            </button>
          </div>

          <div className="mb-8">
            <div className="label-eyebrow mb-2">{t("welcome")}</div>
            <h2 className="text-3xl font-bold tracking-tight">{mode === "login" ? t("login") : mode === "register" ? t("create_account") : t("forgot_password")}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === "login" ? `AgriNova — ${t("slogan")}` : t("register_sub")}
            </p>
          </div>

          <Card className="card-hover border-border">
            <CardContent className="p-6">
              <form onSubmit={mode === "login" ? onSubmit : mode === "register" ? onRegister : onForgot} className="space-y-4" data-testid="login-form">
                {mode === "register" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="name">{t("name")}</Label>
                    <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                      required data-testid="register-name" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("email")}</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required data-testid="login-email" />
                </div>
                {mode !== "forgot" && (
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t("password")}</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    required data-testid="login-password" minLength={mode === "register" ? 8 : undefined} />
                </div>
                )}
                {mode === "login" && (
                  <button type="button" onClick={() => setMode("forgot")}
                    className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                    {t("forgot_password")}
                  </button>
                )}
                <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit">
                  {loading ? "…" : mode === "login" ? t("sign_in") : mode === "register" ? t("create_account") : t("send_reset_link")}
                </Button>
                <button
                  type="button"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="w-full text-center text-sm text-primary hover:underline"
                  data-testid="toggle-register"
                >
                  {mode === "login" ? t("no_account_yet") : t("have_account")}
                </button>
                <Button
                  type="button" variant="outline" className="w-full" data-testid="guest-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/auth/guest");
                      if (data.token) localStorage.setItem("agrinova_token", data.token);
                      window.location.href = "/decouvrir";
                    } catch { toast.error(t("error")); }
                  }}
                >
                  {t("explore_guest")}
                </Button>
              </form>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground uppercase tracking-widest">{t("or_separator")}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <Button
                type="button" variant="outline" className="w-full" data-testid="google-login-btn"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
                {t("google_login")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <ContactFooter />
      <InstallPrompt />
    </div>
  );
}
